<template>
    <div class="flex flex-col lg:flex-row justify-between items-center">
        <h2 class="text-xl leading-9 font-headline font-medium text-bhav-blue-600">Highlights</h2>
        <FormButton v-if="GlobalPolicy.edit()" color="blue" type="button" @click="openCreate">
            <span class="fak fa-plus mr-1"></span>Add
        </FormButton>
    </div>
    <PaginationTable ref="highlightsTable" empty-message="There are currently no highlights." :data="highlights" :columns="columns"
        :view="`${$route.name}-${$route.params.tab}`" :legacy="false">
        <template #col-position="{ row }">
            <div class="flex gap-2 items-center py-4">
                <div class="text-sm leading-5 text-oakwood-gray-900 mr-2">{{ highlights.indexOf(row) + 1 }}</div>
                <button type="button" :disabled="highlights.indexOf(row) == 0" @click="move(row, 'up')"
                    :class="{ 'cursor-pointer': highlights.indexOf(row) > 0, 'text-oakwood-gray-300 cursor-not-allowed': highlights.indexOf(row) == 0 }">
                    <span class="far fa-arrow-up"></span>
                </button>
                <button type="button" :disabled="highlights.indexOf(row) == highlights.length - 1" @click="move(row, 'down')"
                    :class="highlights.indexOf(row) < highlights.length - 1 ? 'cursor-pointer' : 'text-oakwood-gray-300 cursor-not-allowed'">
                    <span class="far fa-arrow-down"></span>
                </button>
            </div>
        </template>

        <template #col-image="{ row }">
            <div class="py-4">
                <div v-if="row.type === 'Text'"
                    class="w-16 h-16 bg-bhav-blue-100 rounded flex items-center justify-center text-bhav-blue-500">
                    <span class="text-2xl font-bold">T</span>
                </div>
                <img v-else-if="row.image" :src="row.image" :alt="row.title"
                    class="w-16 h-16 object-cover rounded" />
                <div v-else
                    class="w-16 h-16 bg-bhav-cool-gray-100 rounded flex items-center justify-center text-bhav-cool-gray-400">
                    <span class="fak fa-photo text-xl"></span>
                </div>
            </div>
        </template>

        <template #col-title="{ row }">
            <div class="py-4 text-bhav-blue-600">
                {{ row.title }}
            </div>
        </template>

        <template #col-type="{ row }">
            <div class="py-4">
                {{ row.type }}
            </div>
        </template>

        <template #col-description="{ row }">
            <div class="py-4 text-sm text-oakwood-gray-500 max-w-xs truncate">
                {{ row.description }}
            </div>
        </template>

        <template #col-actions="{ row }" v-if="GlobalPolicy.edit()">
            <div class="flex gap-2 justify-end py-4">
<FormButton type="button" @click="edit(row)" prefix-class="fak" icon="fa-pen" color="blue">
                    Edit
                </FormButton>
                <FormButton type="button" @click="openDelete(row)" prefix-class="fak" icon="fa-trash" color="red" />
            </div>
        </template>
    </PaginationTable>

    <!-- Create/Edit Modal -->
    <ModalDialog :open="modal" :no-padding="true" @close="cancel">
        <template #header>
            <div>
                <h3 class="text-lg leading-6 font-medium text-bhav-blue-500">
                    {{ isEditing ? 'Edit Highlight' : 'Add Highlight' }}
                </h3>
                <p class="mt-1 max-w-2xl text-sm leading-5 text-oakwood-gray-500">
                    Enter the details for this highlight.
                </p>
            </div>
        </template>
        <template #body>
            <div class="mt-6 sm:mt-5">
                <div
                    class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t w-full sm:border-oakwood-gray-200 sm:py-5">
                    <label for="highlight-type"
                        class="block text-sm font-medium leading-5 text-bhav-blue-500 sm:mt-px sm:pt-2">
                        Type
                    </label>
                    <div class="mt-1 sm:mt-0 sm:col-span-2">
                        <SelectField id="highlight-type" v-model="form.type" :allow-null="false"
                            :options="typeOptions" :float="false" />
                    </div>
                </div>

                <div
                    class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-oakwood-gray-200 sm:py-5">
                    <label for="highlight-title"
                        class="block text-sm font-medium leading-5 text-bhav-blue-500 sm:mt-px sm:pt-2">
                        Title
                    </label>
                    <div class="mt-1 sm:mt-0 sm:col-span-2">
                        <TextInput id="highlight-title" v-model="form.title" />
                    </div>
                </div>

                <div v-if="form.type !== 'Image'"
                    class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-oakwood-gray-200 sm:py-5">
                    <label for="highlight-description"
                        class="block text-sm font-medium leading-5 text-bhav-blue-500 sm:mt-px sm:pt-2">
                        Description
                    </label>
                    <div class="mt-1 sm:mt-0 sm:col-span-2">
                        <textarea id="highlight-description" v-model="form.description"
                            class="bg-bhav-cool-gray-50 text-bhav-blue-500 text-sm w-full p-3 rounded-lg focus:border-bhav-blue-400 min-h-24"></textarea>
                    </div>
                </div>

                <div v-if="form.type === 'Image'"
                    class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-oakwood-gray-200 sm:py-5">
                    <span class="block text-sm font-medium leading-5 text-bhav-blue-500 sm:mt-px sm:pt-2">
                        Image
                    </span>
                    <div class="mt-1 sm:mt-0 sm:col-span-2 grid grid-cols-3 gap-2">
                        <div v-for="image, i in communityAttachments" :key="`hl-img-${i}`" class="transition-opacity"
                            :class="[form.media_item === image.media.id ? '' : 'opacity-40 hover:opacity-100']">
                            <label :for="`hl-image-${i}`" class="relative cursor-pointer w-full aspect-square flex items-center justify-center rounded-lg overflow-hidden border-2"
                                :class="[form.media_item === image.media.id ? 'border-bhav-blue-500' : 'border-transparent']">
                                <img class="object-cover w-full h-full" :src="image.media.url" :alt="image.media.alt" />
                                <div v-if="form.media_item === image.media.id" class="absolute inset-0 bg-bhav-blue-500/50 flex items-center justify-center">
                                    <span class="fas fa-check text-white text-2xl drop-shadow"></span>
                                </div>
                            </label>
                            <input type="radio" :id="`hl-image-${i}`" name="hl-image" v-model="form.media_item" :value="image.media.id" class="sr-only" />
                        </div>
                        <p v-if="!communityAttachments.length" class="col-span-3 text-sm text-bhav-cool-gray-400">
                            No community images available.
                        </p>
                    </div>
                </div>
            </div>
        </template>

        <template #buttons>
            <div class="flex justify-end mt-5 pt-5">
                <span class="inline-flex rounded-md shadow-sm">
                    <FormButton type="button" @click="cancel" color="gray">
                        Cancel
                    </FormButton>
                </span>
                <span class="ml-3 inline-flex rounded-md shadow-sm">
                    <FormButton type="button" @click="save" color="blue">
                        Save
                    </FormButton>
                </span>
            </div>
        </template>
    </ModalDialog>

    <!-- Delete Confirmation Modal -->
    <ModalDialog v-if="deleting" :no-padding="true" :open="deleting !== false" @close="cancel">
        <template #header>
            <h2 class="text-lg text-bhav-blue-500 font-semibold">Confirm Highlight Deletion</h2>
        </template>
        <template #body>
            <p class="text-oakwood-gray-900"><strong>Warning!</strong> You are about to delete
                <em>{{ deleting?.title }}</em>. Are you sure?
            </p>
        </template>
        <template #buttons>
            <FormButton type="button" @click="drop" color="blue">
                Delete
            </FormButton>
            <FormButton type="button" @click="cancel" color="gray">
                Cancel
            </FormButton>
        </template>
    </ModalDialog>
</template>

<script>
import axios from 'axios';
import ModalDialog from '@/components/ModalDialog.vue';
import { FormButton, SelectField, TextInput } from '@/components/form';
import { GlobalPolicy } from '@/policies/global';
import PaginationTable from '@/components/PaginationTable.vue';

const emptyForm = {
    type: 'Text',
    title: '',
    description: '',
    media_item: null,
    attachments: []
};

export default {
    setup() {
        return {
            GlobalPolicy
        }
    },
    data() {
        return {
            highlights: [],
            communityAttachments: [],
            modal: false,
            form: { ...emptyForm },
            editingId: null,
            deleting: false,
            typeOptions: [
                { label: 'Text', value: 'Text' },
                { label: 'Image', value: 'Image' }
            ]
        }
    },
    emits: [
        'update'
    ],
    computed: {
        isEditing() {
            return this.editingId !== null;
        },
        columns() {
            let cols = [
                {
                    name: 'position',
                    label: 'Position',
                    sort: false,
                    default: true
                },
                {
                    name: 'image',
                    label: '',
                    sort: false,
                    default: true
                },
                {
                    name: 'title',
                    label: 'Title',
                    sort: false,
                    default: true
                },
                {
                    name: 'type',
                    label: 'Type',
                    sort: false,
                    default: true
                },
                {
                    name: 'description',
                    label: 'Description',
                    sort: false,
                    default: true
                },
            ];
            if (GlobalPolicy.edit()) {
                cols.push({
                    name: 'actions',
                    label: '',
                    sort: false,
                    default: true,
                    class: 'text-right'
                });
            }
            return cols;
        }
    },
    methods: {
        move(row, direction) {
            const index = this.highlights.indexOf(row);
            const to = direction === 'up' ? index - 1 : index + 2;
            const from = direction === 'up' ? index + 1 : index;
            this.highlights.splice(to, 0, row);
            this.highlights.splice(from, 1);
            axios.put(`divisions/${this.community.id}/highlights/reorder`, {
                highlights: this.highlights.map(h => h.id)
            }).catch(e => {
                console.error('Error updating positions:', e);
                this.index();
            });
        },
        index() {
            return axios.get(`divisions/${this.community.id}/highlights`)
                .then(res => {
                    if (!res.data?.status)
                        this.highlights = res.data.slice().sort((a, b) => (a.meta?.order ?? 0) - (b.meta?.order ?? 0));
                })
                .catch(e => {
                    console.error(e);
                });
        },
        openCreate() {
            this.form = { ...emptyForm };
            this.editingId = null;
            this.modal = true;
        },
        edit(row) {
            this.form = {
                type: row.type,
                title: row.title,
                description: row.description,
                image: row.image,
                attachments: row.attachments || [],
                media_item: row.attachments?.[0]?.media_id ?? null
            };
            this.editingId = row.id;
            this.modal = true;
        },
        validate() {
            if (!this.form.title || !this.form.type) {
                console.error('Missing required values');
                return false;
            }
            return true;
        },
        async save() {
            if (!this.validate()) return;

            let currentOrder = 0;
            if (this.isEditing) {
                const existing = this.highlights.find(h => h.id === this.editingId);
                if (existing?.meta?.order != null) {
                    currentOrder = existing.meta.order;
                } else {
                    currentOrder = this.highlights.indexOf(existing) + 1;
                }
            }

            const payload = {
                type: this.form.type,
                title: this.form.title,
                description: this.form.type === 'Image' ? '' : this.form.description,
                meta: { order: currentOrder, type: this.form.type }
            };

            let highlightId = this.editingId;

            if (this.isEditing) {
                if (this.form.type === 'Text' && this.form.attachments?.length) {
                    for (const attachment of this.form.attachments) {
                        await axios.delete(`attachments/${attachment.id}`)
                            .catch(e => { console.error('Error deleting attachment:', e); });
                    }
                }
                await axios.put(`highlights/${this.editingId}`, payload)
                    .catch(e => { console.error(e); });
            } else {
                const res = await axios.post(`divisions/${this.community.id}/highlights`, payload)
                    .catch(e => { console.error(e); });
                if (res) {
                    highlightId = res.data?.id ?? res.data?.data?.id;
                }
            }

            if (highlightId && this.form.type === 'Image') {
                const existingMediaId = this.form.attachments?.[0]?.media_id ?? null;
                if (this.form.media_item !== existingMediaId) {
                    if (this.form.attachments?.length) {
                        for (const att of this.form.attachments) {
                            await axios.delete(`attachments/${att.id}`)
                                .catch(e => { console.error('Error deleting attachment:', e); });
                        }
                    }
                    if (this.form.media_item) {
                        await axios.patch(`highlights/${highlightId}/attachments`, {
                            attachments: [{ media_id: this.form.media_item, meta: { type: 'URL', order: 0 } }]
                        }).catch(e => { console.error('Error linking attachment:', e); });
                    }
                }
            }

            const currentPage = this.$refs.highlightsTable?.page ?? 1;
            await this.index();
            this.cancel();
            if (currentPage > 1) {
                await this.$nextTick();
                this.$refs.highlightsTable?.setPage(currentPage);
            }
        },
        openDelete(row) {
            this.deleting = row;
        },
        async drop() {
            if (!this.deleting) return;

            const highlight = this.deleting;

            if (highlight.attachments?.length) {
                for (const attachment of highlight.attachments) {
                    await axios.delete(`attachments/${attachment.id}`)
                        .catch(e => {
                            console.error('Error deleting attachment:', e);
                        });
                }
            }

            axios.delete(`highlights/${highlight.id}`)
                .then(() => {
                    this.index();
                    this.cancel();
                })
                .catch(e => {
                    console.error(e);
                });
        },
        cancel() {
            this.form = { ...emptyForm };
            this.editingId = null;
            this.modal = false;
            this.deleting = false;
        }
    },
    props: {
        community: {
            type: Object,
            required: true
        }
    },
    created() {
        this.index();
        axios.get(`divisions/${this.community.id}/attachments`).then(resp => {
            this.communityAttachments = resp.data;
        });
    },
    components: {
        FormButton, ModalDialog, SelectField, TextInput, PaginationTable
    }
}
</script>
